/**
 * Entry point for the Joke Chat UI (Phase 2).
 *
 * Responsibilities:
 * - Keep automatic initialization for production usage.
 * - Load runtime config and initialize the chat UI with the real request client.
 */

import { loadConfig } from "./config.js";
import { logger } from "./logger.js";
import { initChatWithRealClient } from "./chat.js";

// Augment Window with optional test flag for auto initialization control
declare global {
    interface Window {
        __JONAS_AUTO_INIT__?: boolean;
    }
}

/**
 * Auto-initialize the UI for production by wiring it to the real client.
 */
async function init(): Promise<void> {
    // Load runtime config to determine the jokes function endpoint.
    const cfg = await loadConfig();
    const endpoint = cfg.jokesFunctionUrl?.trim() || "";
    if (!endpoint) {
        logger.error("No jokesFunctionUrl provided; chat will initialize with disabled network calls");
    }
    logger.info("Initializing Joke Chat", { endpoint });
    initChatWithRealClient(endpoint, {
        welcomeMessages: cfg.welcomeMessages,
        defaultWelcomeNbas: cfg.defaultWelcomeNbas,
        welcomeMessageDelayMs: cfg.welcomeMessageDelayMs,
    });
}

// Initialize when DOM is ready, unless tests disable it via a global flag
const shouldAutoInit = typeof window !== "undefined" ? (window.__JONAS_AUTO_INIT__ ?? true) : true;
if (shouldAutoInit) {
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => { void init(); });
    } else {
        void init();
    }
}
